import { motion } from "framer-motion";
import { Users, Wallet, CalendarCheck, Bell, Search, GraduationCap, TrendingUp } from "lucide-react";

const BARS = [42, 68, 54, 80, 62, 92, 74, 88, 58, 96, 70, 84];

const LOGS = [
  { t: "09:41", m: "Fee receipt #RC-2081 verified", c: "text-emerald-400" },
  { t: "09:43", m: "Attendance synced — Grade 8B", c: "text-sky-400" },
  { t: "09:47", m: "AI insight: fee defaulter risk ↓ 6%", c: "text-magenta" },
  { t: "09:52", m: "Parent portal login × 38", c: "text-gold-light" },
];

export const DashboardMockup = () => (
  <div data-testid="hero-dashboard-mockup" className="relative">
    {/* desktop frame */}
    <div className="relative overflow-hidden rounded-xl border border-white/10 bg-ink shadow-[0_40px_120px_-30px_rgba(11,18,32,0.9)]">
      <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-magenta/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-gold/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
        <span className="ml-3 font-mono2 text-[10px] tracking-widest text-slate-500">EDUSUITE · SCHOOL ERP · ADMIN CONSOLE</span>
        <span className="ml-auto flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 font-mono2 text-[9px] text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" /> LIVE
        </span>
      </div>
      <div className="grid grid-cols-[52px_1fr]">
        {/* sidebar */}
        <div className="flex flex-col items-center gap-4 border-r border-white/10 py-4">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-gold/15 text-gold"><GraduationCap className="h-4 w-4" /></span>
          <span className="flex h-8 w-8 items-center justify-center rounded-md text-slate-600"><Users className="h-4 w-4" /></span>
          <span className="flex h-8 w-8 items-center justify-center rounded-md text-slate-600"><Wallet className="h-4 w-4" /></span>
          <span className="flex h-8 w-8 items-center justify-center rounded-md text-slate-600"><CalendarCheck className="h-4 w-4" /></span>
          <span className="flex h-8 w-8 items-center justify-center rounded-md text-slate-600"><Bell className="h-4 w-4" /></span>
        </div>
        {/* main */}
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-jakarta text-[13px] font-bold text-white">Greenfield Public School</p>
              <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Academic year 2026–27</p>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-slate-500">
              <Search className="h-3 w-3" />
              <span className="font-mono2 text-[9px]">Search student…</span>
            </div>
          </div>
          {/* stat tiles */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[
              { label: "Students", value: "1,248", icon: Users, accent: "text-gold" },
              { label: "Attendance", value: "96.4%", icon: CalendarCheck, accent: "text-emerald-400" },
              { label: "Fees collected", value: "₹42.6L", icon: Wallet, accent: "text-magenta" },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5">
                <s.icon className={`h-3.5 w-3.5 ${s.accent}`} />
                <p className="mt-1.5 font-jakarta text-sm font-extrabold text-white">{s.value}</p>
                <p className="font-mono2 text-[8px] uppercase tracking-[0.18em] text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          {/* chart */}
          <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.04] p-3">
            <div className="flex items-center justify-between">
              <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Fee collection · monthly</p>
              <span className="flex items-center gap-1 font-mono2 text-[9px] text-emerald-400"><TrendingUp className="h-3 w-3" /> +18%</span>
            </div>
            <div className="mt-2 flex h-16 items-end gap-1.5">
              {BARS.map((h, i) => (
                <span
                  key={i}
                  className="bar-rise flex-1 rounded-sm"
                  style={{
                    height: `${h}%`,
                    animationDelay: `${0.5 + i * 0.07}s`,
                    background: i === 9 ? "linear-gradient(to top,#CC860D,#FE34BC)" : "rgba(204,134,13,0.28)",
                  }}
                />
              ))}
            </div>
          </div>
          {/* live log */}
          <div className="mt-2 space-y-1 rounded-lg border border-white/10 bg-white/[0.04] p-2.5 font-mono2 text-[9px]">
            {LOGS.map((l) => (
              <p key={l.t} className="flex gap-2">
                <span className="text-slate-600">{l.t}</span>
                <span className={l.c}>{l.m}</span>
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* floating mobile card */}
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: -4 }}
      animate={{ opacity: 1, y: 0, rotate: -4 }}
      transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="absolute -bottom-8 -left-4 w-36 rounded-xl border border-white/10 bg-ink/95 p-3 shadow-[0_24px_60px_-20px_rgba(11,18,32,0.85)] backdrop-blur float-y sm:-left-10 sm:w-40"
      data-testid="hero-mobile-mockup"
    >
      <p className="font-mono2 text-[8px] uppercase tracking-[0.2em] text-slate-500">Parent app</p>
      <div className="mt-2 flex items-center gap-2.5">
        <svg viewBox="0 0 36 36" className="h-11 w-11 -rotate-90">
          <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.5" />
          <motion.circle
            cx="18" cy="18" r="15.5" fill="none" stroke="url(#ringGrad)" strokeWidth="3.5" strokeLinecap="round"
            strokeDasharray="97.4" initial={{ strokeDashoffset: 97.4 }} animate={{ strokeDashoffset: 6 }}
            transition={{ delay: 1.5, duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          />
          <defs>
            <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#CC860D" />
              <stop offset="100%" stopColor="#FE34BC" />
            </linearGradient>
          </defs>
        </svg>
        <div>
          <p className="font-jakarta text-base font-extrabold text-white">96%</p>
          <p className="font-mono2 text-[8px] uppercase tracking-widest text-slate-500">Attendance</p>
        </div>
      </div>
      <div className="mt-2.5 space-y-1">
        <div className="h-1.5 w-full rounded bg-white/10"><div className="h-full w-3/4 rounded bg-gold/70" /></div>
        <div className="h-1.5 w-full rounded bg-white/10"><div className="h-full w-1/2 rounded bg-magenta/70" /></div>
      </div>
      <p className="mt-2 rounded-md bg-white/5 px-2 py-1 font-mono2 text-[8px] text-slate-400">Fee due reminder · SMS sent</p>
    </motion.div>

    {/* glow chip */}
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1.4, duration: 0.7 }}
      className="absolute -right-3 -top-5 rounded-lg border border-gold/30 bg-navy/90 px-3 py-2 shadow-lg backdrop-blur sm:-right-6"
      data-testid="hero-ai-chip"
    >
      <p className="font-mono2 text-[8px] uppercase tracking-[0.2em] text-gold-light">AI automation</p>
      <p className="mt-0.5 font-jakarta text-[11px] font-bold text-white">Reports auto-generated</p>
    </motion.div>
  </div>
);
